<template>
    <contact-us-page-ticket-confirmation
        v-if="ticketData !== undefined"
        :ticket="ticketData"
    />

    <div v-else>
        <skeleton v-if="isLoading" />
        <template v-else>
            <anonymous-contact-us-ticket-form
                v-if="viewer === undefined"
                :form-markup="formMarkup"
                @ticket-created="handleTicketCreated"
            />

            <logged-in-contact-us-ticket-form
                v-else
                :form-markup="formMarkup"
                @ticket-created="handleTicketCreated"
            />
        </template>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useViewer } from '~/composables/use-login'
import Skeleton from 'primevue/skeleton'
import { definePageMeta, useRpQuery } from '#imports'
import { useQuery } from '@tanstack/vue-query'
import { graphql } from '~/resources/graphql'
import LoggedInContactUsTicketForm from '~/components/ContactUs/LoggedInContactUsTicketForm.vue'
import { TicketConfirmationTicketFragment } from '~/components/Tickets/TicketConfirmation.vue'
import AnonymousContactUsTicketForm from '~/components/ContactUs/AnonymousContactUsTicketForm.vue'
import ContactUsPageTicketConfirmation from '~/components/Tickets/ContactUsPageTicketConfirmation.vue'
import { useHead } from '@unhead/vue'
import { useSelectedLanguage } from '~/composables/use-selected-language'

useHead({
    title: 'Contact Us'
})

definePageMeta({
    layout: 'org'
})

const query = useRpQuery({ orgLevel: true })
const selectedLanguage = useSelectedLanguage()
const { data, isLoading } = useQuery({
    queryKey: ['ContactUsPageQuery', selectedLanguage],
    placeholderData: previousData => previousData,
    queryFn: () =>
        query(
            graphql(/* GraphQL */ `
                query ContactUsPageQuery($language: Language!) {
                    organization {
                        contactUsFormMarkup(language: $language)
                    }
                }
            `),
            {
                language: selectedLanguage.value
            }
        )
})

const viewer = useViewer()
const formMarkup = computed(() => {
    if (data.value === undefined) {
        throw new Error()
    }

    return data.value.organization.contactUsFormMarkup
})

const ticketData = ref()
function handleTicketCreated(
    createdTicketData: TicketConfirmationTicketFragment
) {
    ticketData.value = createdTicketData
}
</script>
